import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { handleExportClick } from '../service/csvExporter';
import Navbar from '../navbar/Navbar';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';
import Pagination from '../pagination/Pagination';
import Spinner from '../spinner/Spinner';
import Modal from '../modal/Modal';
import logo from '../assets/logo_news_factory.svg';
import { useSelector } from 'react-redux';
import downloadIcon from '../assets/download.svg';
import { AddPublicationButton } from '../addPublicationButton/AddPublicationButton';
import { socialMediaCellRenderer } from '../socialMediaCellRenderer/socialMediaCellRenderer';
import { selectIsAdmin, selectUserGroupement } from '../selectors/userSelectors';
import { buildUsersCollectionUrl, canAccessAllUsers } from './userAccess';

interface UserData {
    id: number;
    active: boolean;
    address?: string;
    agencyName?: string;
    email: string;
    phoneNumber?: string;
    website?: string;
    linkedin?: string;
    insta?: string;
    gmb?: string;
    fb?: string;
    os?: string;
    pendingActivation?: boolean;
    quotaIA?: number;
    requestIA?: number;
    groupement?: string;
    groupementName?: string;
    autoPublish?: boolean;
}

interface GroupementData {
    id: string;
    name: string;
}

const download = { path: downloadIcon };
function Users() {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPage, setTotalPage] = useState(1);
    const [userData, setUserData] = useState<UserData[]>([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState<boolean>(false);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [onlyActive, setOnlyActive] = useState<boolean>(false);
    const [totalItems, setTotalItems] = useState(0);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [currentAgencyName, setCurrentAgencyName] = useState(null);
    const [currentEmail, setCurrentEmail] = useState(null);
    const [currentPhone, setCurrentPhone] = useState(null);
    const [currentAddress, setCurrentAddress] = useState(null);
    const [currentUserActive, setCurrentUserActive] = useState<boolean | null>(null);
    const [currentUserPending, setCurrentUserPending] = useState<boolean | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [groupementsMap, setGroupementsMap] = useState<{
        [key: string]: string;
    }>({});
    const isFetching = useRef(false);

    const adminGrpId = useSelector(selectUserGroupement);
    const urlGroupements = `/api/groupements`;
    const activeUserUrl = `/api/activeUser/`;
    let itemsPerPage = 30;
    const isAdmin = useSelector(selectIsAdmin);
    const hasGlobalUserAccess = canAccessAllUsers(isAdmin, adminGrpId?.id);

    const fetchGroupements = useCallback(async () => {
        try {
            const response = await axios.get(urlGroupements);
            const groupements = response.data['hydra:member'];
            const groupementMap: { [key: string]: string } = {};

            groupements.forEach((groupement: GroupementData) => {
                groupementMap[groupement.id] = groupement.name;
            });

            setGroupementsMap(groupementMap);
        } catch (error) {
            console.error('Erreur lors de la récupération des groupements : ', error);
        }
    }, [urlGroupements]);

    const handleCheckboxClicked = (params: any) => {
        setCurrentUserId(params.data.id);
        setCurrentAgencyName(params.data.agencyName);
        setCurrentEmail(params.data.email);
        setCurrentPhone(params.data.phoneNumber);
        setCurrentAddress(params.data.address);
        setCurrentUserActive(params.data.active);
        setCurrentUserPending(params.data.pendingActivation);
        setModalOpen(true);
    };

    const toogleActivationUser = async () => {
        try {
            const newActiveStatus = !currentUserActive;
            await axios.get(activeUserUrl + `${currentUserId}`);
            setCurrentUserActive(newActiveStatus);
        } catch (error) {
            console.error(error);
        } finally {
            setModalOpen(false);
        }
    };

    const nameCellRenderer = (params: any) => {
        const agencyName = params.data.agencyName;
        return (
            <>
                <span
                    className={
                        'flex items-center h-full w-full justify-start text-base font-semibold mx-4 whitespace-pre-wrap leading-normal'
                    }
                >
                    {agencyName}
                </span>
            </>
        );
    };

    const mailCellRenderer = (params: any) => {
        const email = params.data.email;
        return (
            <>
                <span
                    className={
                        'flex items-center h-full w-full justify-start text-base font-normal whitespace-pre-wrap leading-normal break-words'
                    }
                >
                    {email}
                </span>
            </>
        );
    };

    const phoneCellRenderer = (params: any) => {
        const phoneNumber = params.data.phoneNumber;
        return (
            <>
                <span
                    className={
                        'flex items-center h-full w-full justify-center text-base font-normal'
                    }
                >
                    {phoneNumber}
                </span>
            </>
        );
    };

    const addressCellRenderer = (params: any) => {
        const address = params.data.address;
        return (
            <>
                <span
                    className={
                        'flex items-center h-full w-full justify-start text-base font-normal whitespace-pre-wrap leading-normal'
                    }
                >
                    {address}
                </span>
            </>
        );
    };

    const checkboxRenderer = (params: any) => {
        const handleChange = (e: any) => {
            params.context.handleCheckboxClicked(params);
        };

        return (
            <div className={'checkbox-container'}>
                <input
                    type="checkbox"
                    checked={params.data.active || params.data.pendingActivation}
                    onChange={handleChange}
                    style={params.data.pendingActivation ? { opacity: 0.5 } : {}}
                />
                {params.data.pendingActivation && (
                    <span className="tooltip">Activation en cours...</span>
                )}
            </div>
        );
    };

    const handleQuotaIAChange = (params: any) => {
        const { data } = params;
        axios.patch(
            `/api/users/${data.id}`,
            {
                quotaIA: data.quotaIA,
            },
            {
                headers: {
                    'Content-Type': 'application/merge-patch+json',
                },
            },
        );
    };

    const defaultColDef = useMemo(() => {
        return {
            resizable: false,
            sortable: false,
            suppressMovable: true,
        };
    }, []);

    const groupements = Object.values(groupementsMap);

    const columns: ColDef<UserData>[] = [
        {
            headerName: "Nom de l'agence",
            cellRenderer: nameCellRenderer,
            flex: 2,
        },
        {
            headerName: 'Mail',
            cellRenderer: mailCellRenderer,
            flex: 1,
        },
        {
            headerName: 'Téléphone',
            cellRenderer: phoneCellRenderer,
            flex: 1,
        },
        {
            headerName: 'Adresse',
            cellRenderer: addressCellRenderer,
            flex: 1,
        },
        {
            headerName: 'Réseaux Sociaux',
            cellRenderer: socialMediaCellRenderer,
            flex: 1,
        },
    ];

    if (isAdmin) {
        const adminColumns: ColDef<UserData>[] = [
            {
                headerName: 'Groupement',
                field: 'groupementName',
                editable: params => params.data?.active === true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                    values: groupements,
                },
                onCellValueChanged: async params => {
                    const selectedGroupementName = params.newValue;
                    const selectedGroupementId = Object.keys(groupementsMap).find(
                        key => groupementsMap[key] === selectedGroupementName,
                    );

                    try {
                        await axios.patch(
                            `/api/users/${params.data.id}`,
                            {
                                groupement: `/api/groupements/${selectedGroupementId}`,
                            },
                            {
                                headers: {
                                    'Content-Type': 'application/merge-patch+json',
                                },
                            },
                        );

                        params.data.groupement = `/api/groupements/${selectedGroupementId}`;
                        params.data.groupementName = selectedGroupementName;

                        if (params.node) {
                            params.api.refreshCells({
                                force: true,
                                rowNodes: [params.node],
                            });
                        } else {
                            console.error(
                                'Impossible de rafraîchir la cellule : le noeud est null.',
                            );
                        }
                    } catch (error) {
                        console.error('Erreur lors de la mise à jour du groupement : ', error);
                    }
                },
                flex: 1,
            },
            {
                headerName: 'Requete IA',
                field: 'requestIA',
                flex: 1,
            },
            {
                headerName: 'Quota IA',
                field: 'quotaIA',
                editable: true,
                flex: 1,
                onCellValueChanged: handleQuotaIAChange,
            },
            {
                headerName: 'Activer News Factory',
                field: 'active',
                cellRenderer: checkboxRenderer,
                headerComponent: (params: any) => {
                    return (
                        <span
                            onClick={() => toggleActiveUsers(params)}
                            style={{ cursor: 'pointer', fontWeight: 'bold' }}
                        >
                            Activer News Factory
                        </span>
                    );
                },
                cellRendererParams: {
                    context: {
                        handleCheckboxClicked: handleCheckboxClicked,
                    },
                },
                cellEditor: true,
                flex: 1,
            },
        ];
        columns.push(...adminColumns);
    }

    const toggleActiveUsers = (params: any) => {
        setOnlyActive(prev => !prev);
        params.api.refreshHeader();
    };

    async function fetchAllUsers(): Promise<UserData[]> {
        setLoading(true);
        let users: UserData[] = [];
        let fetchPage = 1;
        let itemsPerPage = 500;
        let hasMore = true;

        while (hasMore) {
            const response = await axios.get(
                buildUsersCollectionUrl(isAdmin, adminGrpId?.id, {
                    page: fetchPage,
                    itemsPerPage,
                }),
            );

            users = users.concat(response.data['hydra:member']);
            setTotalItems(response.data['hydra:totalItems']);
            hasMore = fetchPage * itemsPerPage < totalItems;
            fetchPage++;
        }
        setLoading(false);

        return users;
    }

    const onUsersExport = () => {
        const headers = [
            'id',
            'agencyName',
            'email',
            'phoneNumber',
            'address',
            'website',
            'linkedin',
            'insta',
            'gmb',
            'fb',
            'active',
        ];
        handleExportClick(
            headers,
            fetchAllUsers,
            'users.csv',
            () => setLoading(true),
            () => setLoading(false),
        );
    };

    async function handleSearchText(text: string) {
        setSearchText(text);
        if (text.length >= 3) {
            setIsSearching(true);
        } else {
            setIsSearching(false);
        }
    }

    useEffect(() => {
        if (isFetching.current) {
            return;
        }
        const fetchData = async () => {
            isFetching.current = true;
            try {
                const response = await axios.get(
                    buildUsersCollectionUrl(isAdmin, adminGrpId?.id, {
                        page: currentPage,
                        search: isSearching ? searchText : undefined,
                        onlyActive,
                    }),
                );

                const users = response.data['hydra:member'];
                const usersWithGroupementName = users.map((user: UserData) => ({
                    ...user,
                    groupementName: user.groupement
                        ? groupementsMap[user.groupement.split('/').pop()!]
                        : 'N/A',
                }));

                setTotalItems(response.data['hydra:totalItems']);
                const total = Math.ceil(totalItems / itemsPerPage);
                setTotalPage(total > 0 ? total : 1);
                setUserData(usersWithGroupementName);
            } catch (error) {
                console.error(
                    'Erreur lors de la récupération des données Users depuis Api Platform',
                );
            } finally {
                isFetching.current = false;
            }
        };
        fetchData();
    }, [
        activeUserUrl,
        adminGrpId?.id,
        currentPage,
        groupementsMap,
        isAdmin,
        isSearching,
        itemsPerPage,
        onlyActive,
        searchText,
        totalItems,
    ]);

    useEffect(() => {
        fetchGroupements();
    }, [fetchGroupements]);

    const containerStyle = useMemo(
        () => ({
            width: '100%',
            height: '-webkit-fill-available',
            fontFamily: 'Montserrat',
        }),
        [],
    );

    return (
        <>
            <div className="flex flex-1 relative w-full h-screen">
                <div className="flex-none relative min-w-64">
                    <Navbar />
                </div>
                <div className="flex-1 relative">
                    <div className="flex flex-col items-start relative h-full">
                        <div className="flex flex-col py-10 px-9 w-full">
                            <div className="flex items-center justify-between w-full pb-8 border-b border-light-gray">
                                <div className="flex flex-col items-start gap-1 relative">
                                    <h1 className="text-dark-gray text-super-heavy font-heavy m-0 p-0">
                                        Utilisateurs
                                    </h1>
                                    <p className="text-gray m-0 p-0">
                                        Retrouvez l'ensemble de vos utilisateurs sur cette page
                                    </p>
                                </div>
                                <div>
                                    <AddPublicationButton />
                                </div>
                            </div>
                            <div className={'relative flex w-full justify-between pb-8'}>
                                <input
                                    type="text"
                                    className="flex w-full max-w-80"
                                    placeholder="Nom de l'agence, mail, téléphone..."
                                    value={searchText}
                                    onChange={e => handleSearchText(e.target.value)}
                                />
                                {hasGlobalUserAccess && (
                                    <div className={'flex'}>
                                        <button
                                            className="flex items-center text-dark-gray text-medium font-medium bg-primary-light px-2"
                                            onClick={onUsersExport}
                                        >
                                            <img
                                                src={download.path}
                                                alt="download icon"
                                                className="mr-2 w-8 h-8"
                                            />
                                            Exporter
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div style={containerStyle}>
                                <div className="ag-theme-alpine theme-user" style={containerStyle}>
                                    <Spinner loading={loading} />
                                    <AgGridReact
                                        rowData={userData}
                                        defaultColDef={defaultColDef}
                                        columnDefs={columns}
                                        domLayout="autoHeight"
                                        pagination={false}
                                        overlayNoRowsTemplate={'Pas de données enregistrées'}
                                        suppressNoRowsOverlay={!!userData}
                                    />
                                </div>
                                <Pagination
                                    currentPage={currentPage}
                                    totalPage={totalPage}
                                    previous={() => setCurrentPage(p => p - 1)}
                                    next={() => setCurrentPage(p => p + 1)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
                    <img className="w-[196px] h-auto my-2" src={logo} alt="logo News Factory" />
                    <span className="my-5 text-heavy font-super-heavy">
                        {currentUserActive || currentUserPending ? 'Désactivation ' : 'Activation '}{' '}
                        News Factory
                    </span>
                    <span className="mb-3 text-medium font-medium text-gray">
                        Confirmez-vous{' '}
                        {currentUserActive || currentUserPending
                            ? 'la désactivation de '
                            : "l'accès "}{' '}
                        News Factory au client suivant :{' '}
                    </span>
                    <span className={'mt-[30px] mb-[15px] font-heavy'}>{currentAgencyName}</span>
                    <span className={'text-small text-gray'}>{currentEmail}</span>
                    <span className={'text-small text-gray'}>{currentPhone}</span>
                    <span className={'text-small text-gray mb-[30px]'}>{currentAddress}</span>
                    <div className="flex w-full justify-around">
                        <button
                            className="text-black px-4 py-2 leading-[1.5] rounded-[1.5rem] flex justify-center items-center w-[200px] text-small font-medium mb-2.5 bg-white border border-light-gray"
                            onClick={() => setModalOpen(false)}
                        >
                            Annuler
                        </button>
                        <button
                            className="text-black px-4 py-2 leading-[1.5] rounded-[1.5rem] justify-center items-center w-[200px] text-small font-medium mb-2.5 bg-primary border border-light-gray"
                            onClick={toogleActivationUser}
                        >
                            Je confirme
                        </button>
                    </div>
                </Modal>
            </div>
        </>
    );
}

export default Users;
